/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Rnd } from "react-rnd";
import { Editor, EditorState, RichUtils, convertToRaw, convertFromRaw, ContentState } from "draft-js";
import "draft-js/dist/Draft.css";
import { draftRawToHtml } from "./ReceiptRichTextEditor";
import CredentialedImage from "./CredentialedImage";

const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT_A4 = 707;
const CANVAS_HEIGHT_A5 = 709;
const CANVAS_HEIGHT_A4_LANDSCAPE = 354;
const CANVAS_HEIGHT_A5_LANDSCAPE = 352;
const SNAP_THRESHOLD = 5;
const CENTER_X = CANVAS_WIDTH / 2;

function renderAdditional(content) {
  if (!content) return null;
  if (typeof content === "object" && content.blocks) {
    const html = draftRawToHtml(content);
    if (!html) return null;
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return content;
}

function hasAdditional(content) {
  if (!content) return false;
  if (typeof content === "object" && content.blocks) {
    return content.blocks.some((b) => b.text && b.text.trim());
  }
  return Boolean(content);
}

function renderFixedHeader(el, orgObj) {
  const variant = el.variant || "classic";
  const additional = el.additionalContent;
  const headerColor = el.headerColor || orgObj.headerColor || "333333";
  const buttonColor = el.textColor || orgObj.buttonColor || "ffffff";
  const logo = el.receiptLogo || orgObj.logo || "";
  const showOrgName = el.showOrgName !== false;
  const showAddress = el.showAddress !== false;
  const showIRDNo = el.showIRDNo !== false;
  const orgName = el.customOrgName || orgObj.organizationName || "";
  const address = el.customAddress || orgObj.organizationAddress || "";
  const irdNo = el.customIRDNo || orgObj.organizationIRDNo || "";

  const addressLabel = el.addressLabel || "Organization Address";
  const irdNoLabel = el.irdNoLabel || "IRD File No.";
  const fs = el.fontSize || 6;
  const subFs = Math.max(fs - 1, 4);

  let footerParts = [];
  if (showAddress && address) footerParts.push(addressLabel + ": " + address);
  if (showIRDNo && irdNo) footerParts.push(irdNoLabel + ": " + irdNo);
  const subText = footerParts.length > 0 ? footerParts : null;

  const additionalEl = hasAdditional(additional)
    ? <div style={{ fontSize: subFs, color: "#" + buttonColor, opacity: 0.9, marginTop: 2 }}>{renderAdditional(additional)}</div>
    : null;

  const additionalMinimalEl = hasAdditional(additional)
    ? <div style={{ fontSize: subFs, color: "#868686", marginTop: 2 }}>{renderAdditional(additional)}</div>
    : null;

  if (variant === "centered") {
    return (
      <div style={{ width: "100%", height: "100%", background: "#" + headerColor, overflow: "hidden", borderRadius: 2 }}>
        <div style={{ textAlign: "center", paddingTop: 8 }}>
          {logo && (
            <div style={{ width: 50, height: 50, margin: "0 auto" }}>
              <CredentialedImage src={logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
          )}
          {showOrgName && (
            <div style={{ fontWeight: 600, fontSize: fs + 1, color: "#" + buttonColor, marginTop: 4 }}>
              {orgName}
            </div>
          )}
          {subText && (
            <div style={{ fontSize: subFs, color: "#" + buttonColor, opacity: 0.85, marginTop: 2 }}>
              {subText.map((line, i) => <div key={i}>{line}</div>)}
            </div>
          )}
          {additionalEl}
        </div>
      </div>
    );
  }

  if (variant === "minimal") {
    return (
      <div style={{ width: "100%", height: "100%", overflow: "hidden", borderRadius: 2, borderBottom: "2px solid #" + headerColor }}>
        <div style={{ width: "80%", margin: "0 auto", display: "flex", alignItems: "center", height: "100%" }}>
          <div>
            {showOrgName && <div style={{ fontWeight: 700, fontSize: fs + 3, color: "#" + headerColor }}>{orgName}</div>}
            {subText && (
              <div style={{ fontSize: subFs, color: "#868686", marginTop: 2 }}>
                {subText.map((line, i) => <div key={i}>{line}</div>)}
              </div>
            )}
            {additionalMinimalEl}
          </div>
        </div>
      </div>
    );
  }

  // classic (default)
  return (
    <div style={{ width: "100%", height: "100%", background: "#" + headerColor, overflow: "hidden", borderRadius: 2 }}>
      <div style={{
        width: "80%", margin: "0 auto", position: "relative", height: "100%",
        display: "flex", alignItems: "center"
      }}>
        {logo && (
          <div style={{ width: 65, height: "70%", flexShrink: 0 }}>
            <CredentialedImage src={logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
        )}
        <div style={{
          marginLeft: logo ? 10 : 0,
          fontWeight: 500, fontSize: fs, color: "#" + buttonColor,
          lineHeight: 1.4
        }}>
          {showOrgName && <div>{el.orgNameLabel || "Name of Organization 機構名稱"}: {orgName}</div>}
          {subText && subText.map((line, i) => <div key={i}>{line}</div>)}
          {hasAdditional(additional) && renderAdditional(additional)}
        </div>
      </div>
    </div>
  );
}

function renderFixedHeading(el, receiptType) {
  // Match the PDF default (backend language.json), which uses the same
  // bilingual heading for both donation and payment receipts. Previously the
  // preview showed English-only "Official Receipt" / "Payment Receipt", so an
  // org designing a payment receipt saw a heading the donor never receives.
  const defaultHeading = "Official Receipt 正式收據";
  const heading = el.customHeading || defaultHeading;
  const additional = el.additionalContent;
  return (
    <div style={{ width: "100%", height: "100%", textAlign: "left", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div style={{ fontSize: el.fontSize || 10, fontWeight: 900, marginBottom: 2 }}>{heading}</div>
      {hasAdditional(additional) && (
        <div style={{ fontSize: 7, fontWeight: 400, color: "#495057", marginTop: 2 }}>
          {renderAdditional(additional)}
        </div>
      )}
    </div>
  );
}

function renderFixedReissued(el) {
  // Editor preview always shows a sample (#1) so the org can see how the
  // element will look. Actual PDF only renders when reissueCount > 0.
  const template = el.customText || "Re-issuance #N 補發";
  const sample = template.replace(/#N/g, "#1");
  return (
    <div style={{
      width: "100%",
      height: "100%",
      fontSize: el.fontSize || 6,
      fontWeight: 400,
      color: "#868686",
      textAlign: "left",
      overflow: "hidden",
      lineHeight: 1.2
    }}>
      {sample}
    </div>
  );
}

function renderFixedTable(el, orgObj, nextReceiptNumber, previewDonorType, receiptType) {
  const isOrgPreview = previewDonorType === "organization";
  const displayAsOrg = isOrgPreview;
  const isPayment = receiptType === "payment";
  // Match the PDF defaults (backend language.json): payment receipts label the
  // party "Payer 付款者" (org "付款機構"), donation receipts "Donor 捐款者"
  // (org "捐款機構"). The preview previously always showed the donation labels.
  const donorDefaultEn = isPayment ? "Payer" : "Donor";
  const donorDefaultZh = isPayment ? "付款者" : "捐款者";
  const donorOrgDefaultZh = isPayment ? "付款機構" : "捐款機構";
  const variant = el.variant || "standard";
  const labelWidth = (el.labelWidth || 20) + "%";
  const rowH = el.rowHeight || 50;
  const vr = el.visibleRows || {};
  const cl = el.customLabels || {};

  const getLabel = (key, defaultEn, defaultZh) => [
    (cl[key] && cl[key].en) || defaultEn,
    (cl[key] && (cl[key].zh || cl[key].cn)) || defaultZh
  ];

  const allRows = [
    { key: "receiptNumber", label: getLabel("receiptNumber", "Receipt Number", "收據編號"), data: nextReceiptNumber || "#00001" },
    { key: "transactionType", label: getLabel("transactionType", "Transaction Type", "款項性質"), data: isPayment ? "Payment" : "Donation" },
    { key: "transactionDate", label: getLabel("transactionDate", "Transaction Date", "付款日期 (YYYY/MM/DD)"), data: "2026/01/01" },
    { key: "paymentMethod", label: getLabel("paymentMethod", "Payment Method", "付款方法"), data: "Credit Card" },
    { key: "item", label: getLabel("item", "Item", "項目"), data: "General Fund" },
    { key: "amount", label: getLabel("amount", "Amount", "金額"), data: "HK$100.00" },
    { key: "coverFee", label: getLabel("coverFee", "Cover Fee", "手續費覆蓋"), data: "" },
    { key: "donor", label: displayAsOrg
      ? [(cl.donor && cl.donor.enOrganization) || "Organization", (cl.donor && (cl.donor.zhOrganization || cl.donor.cnOrganization)) || donorOrgDefaultZh]
      : getLabel("donor", donorDefaultEn, donorDefaultZh),
    data: displayAsOrg ? "Sample Corp Ltd" : "John Doe" },
    { key: "donorContactPerson", label: getLabel("donorContactPerson", "Contact Person", "聯絡人"), data: "John Doe" },
    { key: "email", label: getLabel("email", "Email Address", "電郵地址"), data: "john@example.com" },
    { key: "phone", label: getLabel("phone", "Phone Number", "電話號碼"), data: "+852 1234 5678" },
    { key: "note", label: getLabel("note", "Note", "備註"), data: "" }
  ];

  // Add custom fields from org's formSetting
  const vcf = el.visibleCustomFields || {};
  const formSetting = orgObj && orgObj.formSetting;
  if (formSetting && formSetting.enabled && formSetting.rows) {
    formSetting.rows
      // Match the PDF (buildCustomReceipt.js): internal-only fields never
      // appear on the receipt, so exclude them from the preview too.
      .filter((f) => !f.archived && !f.internal && vcf[f.id] !== false)
      .forEach((f) => {
        const labelEn = (f.i18n && f.i18n.label && f.i18n.label.en) || f.id;
        const labelZh = (f.i18n && f.i18n.label && (f.i18n.label.zh || f.i18n.label.cn)) || "";
        allRows.push({ key: "cf_" + f.id, label: [labelEn, labelZh], data: "" });
      });
  }

  const filteredRows = allRows.filter((r) => {
    if (vr[r.key] === false) return false;
    if (r.key === "donorContactPerson" && !isOrgPreview) return false;
    return true;
  });
  const columns = el.columns || 1;

  // Group rows into chunks based on column count
  const chunkedRows = [];
  for (let i = 0; i < filteredRows.length; i += columns) {
    chunkedRows.push(filteredRows.slice(i, i + columns));
  }

  // Match the generated PDF (buildCustomReceipt.js): rows are a fixed height and
  // the element's overflow:hidden clips whatever doesn't fit. Previously the
  // preview scaled rows to fit the box, so shrinking the element hid the
  // clipping that still happens on the real receipt — most often dropping the
  // custom-field rows, which are rendered last.
  const rowHeight = rowH;

  const getRowStyle = (i) => {
    const base = { height: rowHeight };
    if (variant === "bordered") {
      base.borderBottom = "1px solid #e0e0e0";
    }
    if (variant === "striped" && i % 2 === 0) {
      base.background = "#f8f9fa";
    }
    return base;
  };

  const pad = variant === "bordered" ? "4px 0" : 0;
  const pairWidth = 100 / columns;
  const lw = (parseInt(labelWidth) / 100) * pairWidth;
  const vw = pairWidth - lw;

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden", fontSize: el.fontSize || 6 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <tbody>
          {chunkedRows.map((chunk, i) => (
            <tr key={i} style={getRowStyle(i)}>
              {chunk.map((row, j) => (
                <React.Fragment key={j}>
                  <th style={{ textAlign: "left", fontWeight: 400, width: lw + "%", padding: pad }}>
                    <div style={{ color: "#868686", lineHeight: "10px" }}>
                      {row.label[0]}
                      {row.label[1] && <><br /><span>{row.label[1]}</span></>}
                    </div>
                  </th>
                  <th style={{ textAlign: "left", fontWeight: 400, width: vw + "%", padding: pad }}>
                    <span style={{ color: "#000" }}>{row.data}</span>
                  </th>
                </React.Fragment>
              ))}
              {chunk.length < columns && Array.from({ length: columns - chunk.length }).map((_, k) => (
                <React.Fragment key={"empty-" + k}>
                  <th style={{ width: lw + "%", padding: pad }} />
                  <th style={{ width: vw + "%", padding: pad }} />
                </React.Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function getDefaultFooterText(receiptType) {
  // Must match the PDF default footer (backend language.json) verbatim so the
  // preview shows the same number of wrapped lines the org designs against.
  return receiptType === "donation"
    ? "1. This is an electronic receipt. 此為電子收據。\n2. We are a tax exempt charitable organization under Section 88 of the HK Inland Revenue Ordinance. For donation HK$100 or more, you are entitled to apply for tax deduction. 本機構是根據香港《稅務條例》第88條獲豁免繳稅的慈善機構，凡捐款 HK$100 或以上，捐款人可憑正式收據在報稅時申請扣除。"
    : "1. This is an electronic receipt. 此為電子收據。\n2. Please retain this receipt for reference only. 請保留此收據作為付款紀錄。";
}

function renderFixedFooter(el, receiptType) {
  const footerContent = el.footerContent;
  const hasCustomFooter = footerContent && typeof footerContent === "object" && footerContent.blocks &&
    footerContent.blocks.some((b) => b.text && b.text.trim());

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        fontSize: el.fontSize || 6,
        color: "#6c6c6c",
        textAlign: "left",
        lineHeight: 1.5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end"
      }}
    >
      {hasCustomFooter ? (
        <div dangerouslySetInnerHTML={{ __html: draftRawToHtml(footerContent) }} />
      ) : (
        <div>{getDefaultFooterText(receiptType).split("\n").map((line, i) => <div key={i}>{line}</div>)}</div>
      )}
    </div>
  );
}

const INLINE_STYLES = [
  { label: "B", style: "BOLD", css: { fontWeight: "bold" } },
  { label: "I", style: "ITALIC", css: { fontStyle: "italic" } },
  { label: "U", style: "UNDERLINE", css: { textDecoration: "underline" } }
];

function createEditorState(content) {
  if (!content) return EditorState.createEmpty();
  if (typeof content === "object" && content.blocks) {
    return EditorState.createWithContent(convertFromRaw(content));
  }
  return EditorState.createWithContent(ContentState.createFromText(String(content)));
}

function ReceiptCanvas({
  elements,
  selectedElementId,
  onSelectElement,
  onDragStop,
  onResizeStop,
  onUpdateElement,
  organizationObj,
  receiptType,
  readOnly,
  background,
  pageSize = 'A4',
  orientation = 'portrait',
  nextReceiptNumber,
  previewDonorType = "individual",
  onPreviewDonorTypeChange
}) {
  const orgObj = organizationObj || {};
  const isLandscape = orientation === 'landscape';
  const CANVAS_HEIGHT = pageSize === 'A5'
    ? (isLandscape ? CANVAS_HEIGHT_A5_LANDSCAPE : CANVAS_HEIGHT_A5)
    : (isLandscape ? CANVAS_HEIGHT_A4_LANDSCAPE : CANVAS_HEIGHT_A4);
  const CENTER_Y = CANVAS_HEIGHT / 2;
  const [guides, setGuides] = useState({ vLines: [], hLines: [] });
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editorState, setEditorState] = useState(null);

  const startEditing = useCallback((el) => {
    if (readOnly || el.type !== "custom-text") return;
    setEditingId(el.id);
    setEditorState(createEditorState(el.content));
  }, [readOnly]);

  const stopEditing = useCallback(() => {
    if (editingId && editorState && onUpdateElement) {
      const raw = convertToRaw(editorState.getCurrentContent());
      onUpdateElement(editingId, { content: raw });
    }
    setEditingId(null);
    setEditorState(null);
  }, [editingId, editorState, onUpdateElement]);

  const handleEditorChange = useCallback((newState) => {
    setEditorState(newState);
  }, []);

  const toggleInlineStyle = useCallback((style) => {
    if (editorState) {
      setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    }
  }, [editorState]);

  const handleKeyCommand = useCallback((command) => {
    if (editorState) {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        setEditorState(newState);
        return "handled";
      }
    }
    return "not-handled";
  }, [editorState]);

  useEffect(() => {
    const updateScale = () => {
      if (wrapperRef.current) {
        const availableWidth = wrapperRef.current.clientWidth;
        const availableHeight = window.innerHeight - wrapperRef.current.getBoundingClientRect().top - 20;
        const scaleByWidth = availableWidth / CANVAS_WIDTH;
        const scaleByHeight = availableHeight / CANVAS_HEIGHT;
        setScale(Math.min(scaleByWidth, scaleByHeight));
        setWrapperWidth(availableWidth);
      }
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    window.addEventListener("resize", updateScale);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, []);

  const checkGuides = useCallback((x, y, w, h, draggedId) => {
    const elLeft = x;
    const elRight = x + w;
    const elTop = y;
    const elBottom = y + h;
    const elCenterX = x + w / 2;
    const elCenterY = y + h / 2;

    const vLines = [];
    const hLines = [];

    // Canvas center guides
    if (Math.abs(elCenterX - CENTER_X) < SNAP_THRESHOLD) vLines.push(CENTER_X);
    if (Math.abs(elCenterY - CENTER_Y) < SNAP_THRESHOLD) hLines.push(CENTER_Y);

    // Check alignment with other elements
    elements.forEach((other) => {
      if (other.id === draggedId) return;
      if (other.type === "fixed" && other.visible === false) return;

      const oLeft = other.x;
      const oRight = other.x + other.width;
      const oTop = other.y;
      const oBottom = other.y + other.height;
      const oCenterX = other.x + other.width / 2;
      const oCenterY = other.y + other.height / 2;

      // Vertical lines (x-axis alignment)
      if (Math.abs(elLeft - oLeft) < SNAP_THRESHOLD) vLines.push(oLeft);
      if (Math.abs(elRight - oRight) < SNAP_THRESHOLD) vLines.push(oRight);
      if (Math.abs(elLeft - oRight) < SNAP_THRESHOLD) vLines.push(oRight);
      if (Math.abs(elRight - oLeft) < SNAP_THRESHOLD) vLines.push(oLeft);
      if (Math.abs(elCenterX - oCenterX) < SNAP_THRESHOLD) vLines.push(oCenterX);

      // Horizontal lines (y-axis alignment)
      if (Math.abs(elTop - oTop) < SNAP_THRESHOLD) hLines.push(oTop);
      if (Math.abs(elBottom - oBottom) < SNAP_THRESHOLD) hLines.push(oBottom);
      if (Math.abs(elTop - oBottom) < SNAP_THRESHOLD) hLines.push(oBottom);
      if (Math.abs(elBottom - oTop) < SNAP_THRESHOLD) hLines.push(oTop);
      if (Math.abs(elCenterY - oCenterY) < SNAP_THRESHOLD) hLines.push(oCenterY);
    });

    // Deduplicate
    setGuides({
      vLines: [...new Set(vLines)],
      hLines: [...new Set(hLines)]
    });
  }, [elements, CENTER_Y]);

  const clearGuides = useCallback(() => {
    setGuides({ vLines: [], hLines: [] });
  }, []);

  return (
    <div
      className="ReceiptCanvas-wrapper"
      ref={wrapperRef}
      style={{
        width: "100%",
        height: CANVAS_HEIGHT * scale
      }}
    >
      <div
        className="ReceiptCanvas-canvas"
        style={{
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          position: "relative",
          background: "#ffffff",
          border: "1px solid #d0d0d0",
          boxShadow: "0px 3px 12px rgba(0,0,0,0.12)",
          overflow: "hidden",
          transformOrigin: "top left",
          transform: `scale(${scale})`,
          marginLeft: Math.max((wrapperWidth - CANVAS_WIDTH * scale) / 2, 0),
          fontFamily: "'Noto Sans TC', sans-serif"
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (editingId) stopEditing();
            onSelectElement(null);
          }
        }}
      >
        {/* Full-canvas background image (behind all elements) */}
        {background && background.src && (
          <CredentialedImage
            src={background.src}
            alt="receipt background"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: background.opacity != null ? background.opacity : 1,
              zIndex: 0,
              pointerEvents: "none"
            }}
          />
        )}

        {/* Inline editing toolbar */}
        {editingId && editorState && (() => {
          const editEl = elements.find((e) => e.id === editingId);
          if (!editEl) return null;
          const currentStyle = editorState.getCurrentInlineStyle();
          return (
            <div
              className="ReceiptCanvas-inlineToolbar"
              style={{
                position: "absolute",
                left: editEl.x,
                top: Math.max(editEl.y - 36, 0),
                zIndex: 100
              }}
            >
              {INLINE_STYLES.map((type) => (
                <button
                  key={type.style}
                  type="button"
                  className={
                    "ReceiptRichText-btn" +
                    (currentStyle.has(type.style) ? " ReceiptRichText-btn--active" : "")
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggleInlineStyle(type.style);
                  }}
                  style={type.css}
                >
                  {type.label}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Alignment guides */}
        {guides.vLines && guides.vLines.map((x, i) => (
          <div key={"v" + i} className="ReceiptCanvas-guide ReceiptCanvas-guide--vertical"
            style={{ left: x }} />
        ))}
        {guides.hLines && guides.hLines.map((y, i) => (
          <div key={"h" + i} className="ReceiptCanvas-guide ReceiptCanvas-guide--horizontal"
            style={{ top: y }} />
        ))}

        {elements.map((el) => {
          if (el.type === "fixed" && el.visible === false) return null;

          const isSelected = el.id === selectedElementId;
          const isFixed = el.type === "fixed";
          const isLocked = isFixed && el.tag === "header";
          const isDraggable = !isLocked;
          const isResizable = !isFixed || el.tag === "table" || el.tag === "header" || el.tag === "heading" || el.tag === "footer" || el.tag === "reissued";

          // Header is always pinned to top-left, full width
          const elX = isLocked ? 0 : el.x;
          const elY = isLocked ? 0 : el.y;
          const elWidth = isLocked ? 500 : el.width;

          return (
            <Rnd
              key={el.id}
              size={{ width: elWidth, height: el.height }}
              position={{ x: elX, y: elY }}
              bounds="parent"
              scale={scale}
              disableDragging={readOnly || !isDraggable || editingId === el.id}
              enableResizing={!readOnly && isResizable && editingId !== el.id
                ? (isLocked
                  ? { bottom: true, top: false, left: false, right: false, topLeft: false, topRight: false, bottomLeft: false, bottomRight: false }
                  : true)
                : false}
              lockAspectRatio={el.type === "custom-image"}
              onDrag={(e, d) => {
                if (isDraggable) {
                  checkGuides(d.x, d.y, el.width, el.height, el.id);
                }
              }}
              onDragStop={(e, d) => {
                clearGuides();
                if (isDraggable) onDragStop(el.id, d.x, d.y);
              }}
              onResize={(e, direction, ref, delta, position) => {
                checkGuides(
                  position.x, position.y,
                  parseInt(ref.style.width, 10),
                  parseInt(ref.style.height, 10),
                  el.id
                );
              }}
              onResizeStop={(e, direction, ref, delta, position) => {
                clearGuides();
                if (isResizable) {
                  onResizeStop(
                    el.id,
                    isLocked ? 500 : parseInt(ref.style.width, 10),
                    parseInt(ref.style.height, 10),
                    isLocked ? 0 : position.x,
                    isLocked ? 0 : position.y
                  );
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (editingId && editingId !== el.id) stopEditing();
                onSelectElement(el.id);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (el.type === "custom-text") startEditing(el);
              }}
              style={{
                outline: readOnly
                  ? "none"
                  : isSelected
                    ? "2px solid #1390e3"
                    : isFixed ? "none" : "1px dashed #ccc",
                background: readOnly
                  ? "transparent"
                  : isSelected
                    ? "rgba(19,144,227,0.04)"
                    : "transparent",
                cursor: readOnly ? "default" : isDraggable ? "move" : "default"
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  userSelect: editingId === el.id ? "auto" : "none",
                  pointerEvents: editingId === el.id ? "auto" : "none"
                }}
              >
                {el.type === "fixed" && el.tag === "header" &&
                  renderFixedHeader(el, orgObj)}
                {el.type === "fixed" && el.tag === "heading" &&
                  renderFixedHeading(el, receiptType)}
                {el.type === "fixed" && el.tag === "reissued" &&
                  renderFixedReissued(el)}
                {el.type === "fixed" && el.tag === "table" &&
                  renderFixedTable(el, orgObj, nextReceiptNumber, previewDonorType, receiptType)}
                {el.type === "fixed" && el.tag === "footer" &&
                  renderFixedFooter(el, receiptType)}

                {el.type === "custom-text" && editingId === el.id && editorState ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: 4,
                      boxSizing: "border-box",
                      fontSize: (el.style && el.style.fontSize) || 12,
                      color: (el.style && el.style.color) || "#000000",
                      textAlign: (el.style && el.style.textAlign) || "left",
                      overflow: "hidden",
                      wordWrap: "break-word",
                      lineHeight: 1.4,
                      cursor: "text"
                    }}
                  >
                    <Editor
                      editorState={editorState}
                      onChange={handleEditorChange}
                      handleKeyCommand={handleKeyCommand}
                    />
                  </div>
                ) : el.type === "custom-text" ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: 4,
                      boxSizing: "border-box",
                      fontSize: (el.style && el.style.fontSize) || 12,
                      color: (el.style && el.style.color) || "#000000",
                      textAlign: (el.style && el.style.textAlign) || "left",
                      overflow: "hidden",
                      wordWrap: "break-word",
                      lineHeight: 1.4
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        el.content && typeof el.content === "object"
                          ? draftRawToHtml(el.content)
                          : (el.content || "")
                    }}
                  />
                ) : null}
                {el.type === "custom-image" && el.src && (
                  <CredentialedImage
                    src={el.src}
                    alt="custom"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain"
                    }}
                  />
                )}
              </div>
            </Rnd>
          );
        })}
      </div>
    </div>
  );
}

ReceiptCanvas.propTypes = {
  elements: PropTypes.array.isRequired,
  selectedElementId: PropTypes.string,
  onSelectElement: PropTypes.func.isRequired,
  onDragStop: PropTypes.func.isRequired,
  onResizeStop: PropTypes.func.isRequired,
  onUpdateElement: PropTypes.func,
  organizationObj: PropTypes.object,
  receiptType: PropTypes.string,
  readOnly: PropTypes.bool,
  background: PropTypes.shape({
    src: PropTypes.string,
    opacity: PropTypes.number
  }),
  pageSize: PropTypes.string,
  orientation: PropTypes.string,
  nextReceiptNumber: PropTypes.string,
  previewDonorType: PropTypes.string,
  onPreviewDonorTypeChange: PropTypes.func
};

export default ReceiptCanvas;
